import { Link } from "react-router-dom";

interface ServiceArea {
  name: string;
}

interface ServiceAreasSectionProps {
  heading?: string;
  subheading?: string;
  geoCopy?: string;
  areas?: ServiceArea[];
}

const defaultAreas: ServiceArea[] = [
  { name: "Orlando" },
  { name: "Winter Park" },
  { name: "Kissimmee" },
  { name: "Sanford" },
  { name: "Lake Mary" },
  { name: "Oviedo" },
  { name: "Titusville" },
  { name: "Melbourne" },
  { name: "Palm Bay" },
  { name: "Daytona Beach" },
  { name: "DeLand" },
  { name: "Holly Hill" },
];

export default function ServiceAreasSection({
  heading = "Roofing Services Near You",
  subheading = "Serving homeowners across Central Florida — Orange, Seminole, Brevard, Volusia, Lake & Osceola Counties",
  geoCopy = "If you're searching for roofing repair near me in the Orlando area or anywhere in Central Florida, Thomas Roofing & Repair provides fast, reliable service throughout the region. Our local crews are based in Orange, Brevard, and Volusia Counties and know Florida's roofing challenges inside and out.",
  areas = defaultAreas,
}: ServiceAreasSectionProps) {
  return (
    <section className="bg-[#F8F9FA] py-0 overflow-hidden">
      {/* Full-width Map — Orlando, FL */}
      <div className="w-full h-[420px] relative">
        <iframe
          title="Thomas Roofing Service Area Map"
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d224104.5!2d-81.37588!3d28.53834!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88e773c8b7d3551d%3A0x3f0b2ea3e4bbc94c!2sOrlando%2C%20FL!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
          width="100%"
          height="100%"
          style={{ border: 0, display: "block" }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        ></iframe>
        {/* Overlay badge */}
        <div className="absolute top-5 left-5 bg-[#1A1A1A]/90 backdrop-blur-sm text-white rounded-lg px-5 py-3 flex items-center gap-3">
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-map-pin-fill text-[#10B981] text-base"></i>
          </div>
          <div>
            <span className="font-headline font-700 text-white text-sm block">Central Florida</span>
            <span className="text-white/60 font-body text-xs">Orlando &amp; Surrounding Counties</span>
          </div>
        </div>
      </div>

      {/* Content below map */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <span className="text-[#10B981] font-body font-600 text-sm tracking-widest uppercase mb-3 block">
              Service Areas
            </span>
            <h2 className="font-headline font-800 text-[#1A1A1A] text-3xl lg:text-4xl xl:text-5xl mb-5 leading-tight">
              {heading}
            </h2>
            <p className="text-[#6B7280] font-body text-sm lg:text-base leading-relaxed mb-4">
              {subheading}
            </p>
            <p className="text-[#6B7280] font-body text-sm leading-relaxed mb-8">
              {geoCopy}
            </p>
            <div className="flex items-center gap-3">
              <a
                href="tel:407-408-0577"
                className="inline-flex items-center gap-2 bg-[#1A1A1A] hover:bg-[#2d2d2d] text-white font-body font-700 text-sm px-6 py-3.5 rounded-md cursor-pointer whitespace-nowrap transition-colors"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-fill text-[#10B981] text-sm"></i>
                </div>
                Call (407) 408-0577
              </a>
              <Link
                to="/#contact"
                className="text-[#10B981] hover:text-emerald-600 font-body font-600 text-sm cursor-pointer whitespace-nowrap transition-colors"
              >
                Free Inspection →
              </Link>
            </div>
          </div>

          <div>
            <p className="font-body font-600 text-[#1A1A1A] text-sm mb-4 uppercase tracking-wider">Areas We Serve</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {areas.map((area) => (
                <div
                  key={area.name}
                  className="flex items-center gap-2 bg-white rounded-md px-4 py-3 border border-gray-100 hover:border-[#10B981]/40 transition-colors"
                >
                  <div className="w-4 h-4 flex items-center justify-center shrink-0">
                    <i className="ri-map-pin-fill text-[#10B981] text-sm"></i>
                  </div>
                  <span className="font-body text-[#1A1A1A] text-sm font-500 whitespace-nowrap">{area.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
